import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Button, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';

export const ACCESS_NONE = 'none';
export const ACCESS_ALL = 'all';
export const ACCESS_SELECTED = 'selected';

// One product's school access, as a tri-state: No access / All schools / Selected schools only.
// The picker appears only for the third choice. Rendered once for Spark and once for Pulse — the
// two products' access reads identically, and "no access" is an explicit choice rather than an
// implicit empty form (this replaces the old per-product checkbox + the pulse enable toggle).
//
// The picker is freeSolo over the org-wide school superset (both products' names come from
// `survey_participants.school`). NOTE for Spark: the API scopes by EXACT match against the mart
// keys, so a free-typed name that doesn't match a suggestion grants no data — prefer the list.
//
// `copyFromLabel` / `onCopyFrom` add the "Copy schools from <other product>" shortcut in the
// header; the parent decides whether the other product has a list worth offering, and owns the
// confirmation, because only the parent knows both sides.
const SchoolAccessSection = ({
  title,
  mode,
  onModeChange,
  schools,
  onSchoolsChange,
  options = [],
  errorText = null,
  copyFromLabel = null,
  onCopyFrom = null,
}) => {
  // Already-saved values missing from the option list (legacy free-text names) must stay visible
  // and removable rather than be silently dropped by the picker.
  const mergedOptions = [...new Set([...options, ...schools])];

  const handleSchoolsChange = (event, value) => {
    onSchoolsChange([...new Set(value.map((school) => school.trim()).filter(Boolean))]);
  };

  const helperText = () => {
    if (errorText) return errorText;
    if (schools.length === 0) return 'No schools selected yet — this viewer will see no data';
    return 'Pick a school or type a name and press Enter';
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 3 }}>
        <Typography variant="h6">{title}</Typography>
        {copyFromLabel && onCopyFrom && (
          <Button size="small" onClick={onCopyFrom} startIcon={<FontAwesomeIcon icon={faCopy} />} sx={{ flexShrink: 0 }}>
            Copy schools from {copyFromLabel}
          </Button>
        )}
      </Stack>
      <RadioGroup row value={mode} onChange={(event) => onModeChange(event.target.value)}>
        <FormControlLabel value={ACCESS_NONE} control={<Radio />} label="No access" />
        <FormControlLabel value={ACCESS_ALL} control={<Radio />} label="All schools" />
        <FormControlLabel value={ACCESS_SELECTED} control={<Radio />} label="Selected schools only" />
      </RadioGroup>
      {mode === ACCESS_SELECTED && (
        <Autocomplete
          multiple
          freeSolo
          options={mergedOptions}
          value={schools}
          onChange={handleSchoolsChange}
          size="small"
          sx={{ mt: 1 }}
          renderInput={(params) => <TextField {...params} label="Schools" error={!!errorText} helperText={helperText()} />}
        />
      )}
    </>
  );
};

SchoolAccessSection.propTypes = {
  title: PropTypes.string.isRequired,
  mode: PropTypes.oneOf([ACCESS_NONE, ACCESS_ALL, ACCESS_SELECTED]).isRequired,
  onModeChange: PropTypes.func.isRequired,
  schools: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSchoolsChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.string),
  errorText: PropTypes.string,
  copyFromLabel: PropTypes.string,
  onCopyFrom: PropTypes.func,
};

export default SchoolAccessSection;
